import React, { useEffect, useMemo, useState } from "react";
import { MdClose } from "react-icons/md";
import api from "../../../../services/axios";
import {
  Billing,
  ClientEquipmentOption,
  ClientTimesheetDetail,
  UpdateServiceOrderPayload,
} from "../../../../interfaces/Financial";
import { toast } from "react-toastify";

const fmtBRL = (v: number) =>
  v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

interface ServiceOrderEditModalProps {
  billing: Billing;
  onClose: () => void;
  onSuccess: () => void;
}

const ServiceOrderEditModal: React.FC<ServiceOrderEditModalProps> = ({
  billing,
  onClose,
  onSuccess,
}) => {
  const [details, setDetails] = useState<ClientTimesheetDetail[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedDetailIds, setSelectedDetailIds] = useState<Set<number>>(
    new Set(billing.timeSheetDetails.map((d) => d.timeSheetDetailId))
  );
  const [equipmentOptions, setEquipmentOptions] = useState<ClientEquipmentOption[]>([]);
  const [equipmentsLoading, setEquipmentsLoading] = useState(false);
  const [selectedEquipmentIds, setSelectedEquipmentIds] = useState<Set<number>>(
    new Set(billing.equipments.map((e) => e.equipmentId))
  );
  const [amount, setAmount] = useState(String(billing.amount));
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await api.get<ClientTimesheetDetail[]>(
          `/Financial/ClientTimesheetDetails`,
          { params: { clientId: billing.clientId, month: billing.monthRef, year: billing.yearRef } }
        );
        setDetails(res.data);
      } catch {
        toast.error("Failed to load timesheet entries.", {
          position: "top-right",
          autoClose: 4000,
        });
      } finally {
        setLoading(false);
      }
    };
    const loadEquipments = async () => {
      setEquipmentsLoading(true);
      try {
        const res = await api.get<ClientEquipmentOption[]>(
          `/Financial/ClientEquipmentOptions`,
          { params: { clientId: billing.clientId, month: billing.monthRef, year: billing.yearRef } }
        );
        setEquipmentOptions(res.data);
      } catch {
        setEquipmentOptions([]);
      } finally {
        setEquipmentsLoading(false);
      }
    };
    load();
    loadEquipments();
  }, [billing.clientId, billing.monthRef, billing.yearRef]);

  const totalEquipmentValue = useMemo(
    () =>
      equipmentOptions
        .filter((e) => selectedEquipmentIds.has(e.equipmentId))
        .reduce((acc, e) => acc + e.monthlyValue, 0),
    [equipmentOptions, selectedEquipmentIds]
  );

  const toggleEquipment = (id: number) => {
    setSelectedEquipmentIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const isEquipmentAvailable = (e: ClientEquipmentOption) =>
    !e.alreadyBilledThisMonth || e.linkedBillingId === billing.billingId;

  const totalHours = useMemo(
    () =>
      details
        .filter((d) => selectedDetailIds.has(d.timeSheetDetailId))
        .reduce((acc, d) => acc + d.hoursWorked, 0),
    [details, selectedDetailIds]
  );

  const orderedDetails = useMemo(
    () =>
      [...details].sort((a, b) => {
        if (a.employeeName !== b.employeeName) return a.employeeName.localeCompare(b.employeeName);
        if (a.projectName !== b.projectName) return a.projectName.localeCompare(b.projectName);
        return a.dayRef - b.dayRef;
      }),
    [details]
  );

  const toggleDetail = (id: number) => {
    setSelectedDetailIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const isAvailable = (d: ClientTimesheetDetail) =>
    d.linkedBillingId === null || d.linkedBillingId === billing.billingId;

  // Apontamentos disponíveis = não vinculados a outra cobrança (ou já desta OS), de todos os projetos do cliente.
  const availableDetailIds = useMemo(
    () =>
      details
        .filter((d) => d.linkedBillingId === null || d.linkedBillingId === billing.billingId)
        .map((d) => d.timeSheetDetailId),
    [details, billing.billingId]
  );
  const allHoursSelected =
    availableDetailIds.length > 0 && availableDetailIds.every((id) => selectedDetailIds.has(id));

  const toggleAllHours = () => {
    setSelectedDetailIds((prev) => {
      const next = new Set(prev);
      if (allHoursSelected) availableDetailIds.forEach((id) => next.delete(id));
      else availableDetailIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = parseFloat(amount.replace(",", "."));
    if (isNaN(parsed) || parsed <= 0) {
      toast.warn("Amount must be greater than zero.", { position: "top-right", autoClose: 4000 });
      return;
    }

    const payload: UpdateServiceOrderPayload = {
      amount: parsed,
      timeSheetDetailIds: Array.from(selectedDetailIds),
      equipmentIds: Array.from(selectedEquipmentIds),
    };

    setSubmitting(true);
    try {
      await api.patch(`/Financial/ServiceOrder/${billing.billingId}`, payload);
      toast.success("Service Order updated.", { position: "top-right", autoClose: 4000 });
      onSuccess();
      onClose();
    } catch (err: any) {
      const msg = err?.response?.data;
      toast.error(typeof msg === "string" ? msg : "Failed to update Service Order.", {
        position: "top-right",
        autoClose: 4000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/60 p-4 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="relative flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-dv-lg dark:bg-navy-800">
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4 dark:border-gray-700">
          <div>
            <h2 className="text-lg font-bold text-navy-700 dark:text-white">
              Editar OS #{billing.billingId}
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {billing.clientName} · {billing.monthRef.toString().padStart(2, "0")}/{billing.yearRef}
            </p>
          </div>
          <button
            aria-label="Fechar"
            onClick={onClose}
            className="rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-navy-700 dark:hover:text-white"
          >
            <MdClose className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-1 flex-col overflow-hidden">
          <div className="flex-1 space-y-4 overflow-y-auto px-6 py-5">
            <div>
              <label htmlFor="so-edit-amount" className="mb-1.5 block text-sm font-semibold text-gray-700 dark:text-gray-300">
                Valor
              </label>
              <div className="relative">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">
                  R$
                </span>
                <input
                  id="so-edit-amount"
                  type="number"
                  required
                  min="0.01"
                  step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="w-full rounded-xl border border-gray-200 bg-white py-2.5 pl-9 pr-3 text-sm text-gray-800 transition-all focus:border-dv-blue-500 focus:outline-none focus:ring-2 focus:ring-dv-blue-500/20 dark:border-white/10 dark:bg-navy-900 dark:text-white"
                />
              </div>
            </div>

            <div>
              <div className="mb-2 flex items-center justify-between gap-2">
                <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Apontamentos vinculados
                </label>
                {availableDetailIds.length > 0 && (
                  <label className="flex cursor-pointer items-center gap-1.5 text-xs font-semibold text-dv-blue-600 dark:text-dv-cyan-300">
                    <input
                      type="checkbox"
                      checked={allHoursSelected}
                      onChange={toggleAllHours}
                      className="h-3.5 w-3.5 rounded border-gray-300 text-dv-blue-600 accent-dv-blue-600 focus:ring-dv-blue-500"
                    />
                    Incluir todas as horas apontadas dos projetos deste cliente
                  </label>
                )}
              </div>
              <div className="rounded-xl border border-gray-200 dark:border-white/10">
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="h-6 w-6 animate-spin rounded-full border-2 border-dv-blue-500 border-t-transparent" />
                  </div>
                ) : orderedDetails.length === 0 ? (
                  <p className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                    Nenhum apontamento disponível.
                  </p>
                ) : (
                  <div className="max-h-72 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="sticky top-0 bg-gray-50 dark:bg-navy-900">
                        <tr>
                          <th className="w-10 px-3 py-2"></th>
                          <th className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-gray-300">
                            Colaborador
                          </th>
                          <th className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-gray-300">
                            Projeto
                          </th>
                          <th className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-gray-300">
                            Dia
                          </th>
                          <th className="px-3 py-2 text-right font-semibold text-gray-700 dark:text-gray-300">
                            Horas
                          </th>
                          <th className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-gray-300">
                            Descrição
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {orderedDetails.map((d) => {
                          const checked = selectedDetailIds.has(d.timeSheetDetailId);
                          const available = isAvailable(d);
                          return (
                            <tr
                              key={d.timeSheetDetailId}
                              className={`border-t border-gray-100 dark:border-gray-700 ${
                                !available ? "opacity-50" : ""
                              }`}
                            >
                              <td className="px-3 py-2">
                                <input
                                  type="checkbox"
                                  disabled={!available}
                                  checked={checked}
                                  onChange={() => toggleDetail(d.timeSheetDetailId)}
                                  className="h-4 w-4 rounded border-gray-300 text-dv-blue-600 accent-dv-blue-600 focus:ring-dv-blue-500"
                                />
                              </td>
                              <td className="px-3 py-2 text-gray-700 dark:text-gray-300">
                                {d.employeeName}
                              </td>
                              <td className="px-3 py-2 text-gray-700 dark:text-gray-300">
                                {d.projectName}
                              </td>
                              <td className="px-3 py-2 text-gray-600 dark:text-gray-400">
                                {String(d.dayRef).padStart(2, "0")}
                              </td>
                              <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300">
                                {d.hoursWorked.toFixed(1)}h
                              </td>
                              <td className="px-3 py-2 text-gray-600 dark:text-gray-400" title={d.dsActivity ?? ""}>
                                {!available ? (
                                  <span className="text-xs italic text-gray-500 dark:text-gray-400">
                                    Vinculado à cobrança #{d.linkedBillingId}
                                  </span>
                                ) : (
                                  d.dsActivity || "—"
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                {selectedDetailIds.size} item(ns) selecionado(s) · {totalHours.toFixed(1)} horas no total
              </p>
            </div>

            <div>
              <label className="mb-2 block text-sm font-semibold text-gray-700 dark:text-gray-300">
                Equipamentos do cliente
              </label>
              <div className="rounded-xl border border-gray-200 dark:border-white/10">
                {equipmentsLoading ? (
                  <div className="flex items-center justify-center py-6">
                    <div className="h-5 w-5 animate-spin rounded-full border-2 border-dv-blue-500 border-t-transparent" />
                  </div>
                ) : equipmentOptions.length === 0 ? (
                  <p className="px-4 py-4 text-center text-xs italic text-gray-500 dark:text-gray-400">
                    Nenhum equipamento alugado configurado para este cliente.
                  </p>
                ) : (
                  <div className="max-h-56 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="sticky top-0 bg-gray-50 dark:bg-navy-900">
                        <tr>
                          <th className="w-10 px-3 py-2"></th>
                          <th className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-gray-300">
                            Equipamento
                          </th>
                          <th className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-gray-300">
                            Nº Série
                          </th>
                          <th className="px-3 py-2 text-right font-semibold text-gray-700 dark:text-gray-300">
                            Valor mensal
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {equipmentOptions.map((e) => {
                          const checked = selectedEquipmentIds.has(e.equipmentId);
                          const available = isEquipmentAvailable(e);
                          return (
                            <tr
                              key={e.equipmentId}
                              className={`border-t border-gray-100 dark:border-gray-700 ${
                                !available ? "opacity-50" : ""
                              }`}
                              title={
                                !available
                                  ? `Já cobrado em OS #${e.linkedBillingId} este mês`
                                  : ""
                              }
                            >
                              <td className="px-3 py-2">
                                <input
                                  type="checkbox"
                                  disabled={!available}
                                  checked={checked}
                                  onChange={() => toggleEquipment(e.equipmentId)}
                                  className="h-4 w-4 rounded border-gray-300 text-dv-blue-600 accent-dv-blue-600 focus:ring-dv-blue-500"
                                />
                              </td>
                              <td className="px-3 py-2 text-gray-700 dark:text-gray-300">
                                {e.model}
                              </td>
                              <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{e.serieNumber}</td>
                              <td className="px-3 py-2 text-right font-medium text-gray-700 dark:text-gray-300">
                                {fmtBRL(e.monthlyValue)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                {selectedEquipmentIds.size} equipamento(s) · {fmtBRL(totalEquipmentValue)}
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-3 border-t border-gray-100 px-6 py-4 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-semibold text-gray-700 transition-all hover:bg-gray-50 active:scale-[.98] dark:border-white/15 dark:text-gray-200 dark:hover:bg-navy-700"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex items-center justify-center gap-2 rounded-xl bg-ink-900 px-5 py-2.5 text-sm font-bold text-white shadow-dv-md transition-all hover:bg-ink-800 active:scale-[.98] disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white dark:text-ink-900 dark:hover:bg-gray-100"
            >
              {submitting ? "Salvando…" : "Salvar alterações"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ServiceOrderEditModal;
